import {z} from "zod";
import {v4 as UUID} from "uuid";
import {imageSchema} from "@/images/ImageSchema.ts";
import {createEmptyLevelGeometry} from "@/dungeons/model/DungeonGeometry.ts";
import {validTransitionPolygon} from "@/dungeons/geometry/TransitionPolygon.ts";
import {createDirectionalLight, directionalLightSchema} from "@/dungeons/lighting/DirectionalLight.ts";

const pointSchema = z.object({x: z.number(), y: z.number()});
const idSchema = z.uuid();
const geometrySchema = z.object({
    schemaVersion: z.literal(1),
    directionalLight: directionalLightSchema.default(createDirectionalLight),
    grid: z.object({type: z.literal("SQUARE"), metersPerCell: z.number().positive()}),
    registration: z.object({
        pixelsPerCell: z.number().positive(),
        gridOffsetX: z.number(),
        gridOffsetY: z.number(),
        calibrated: z.boolean(),
    }),
    walls: z.array(z.object({id: idSchema, points: z.array(pointSchema).min(2)})),
    doors: z.array(z.object({
        id: idSchema, start: pointSchema, end: pointSchema,
        initialState: z.enum(["OPEN", "CLOSED"]), secret: z.boolean(),
        label: z.string().max(160).nullish(), notes: z.string().max(20_000).nullish(),
    })),
    transitions: z.array(z.object({
        id: idSchema, points: z.array(pointSchema).min(3),
        kind: z.enum(["STAIRS_UP", "STAIRS_DOWN", "LADDER", "ELEVATOR", "OTHER"]),
        label: z.string().max(160).nullish(), targetLevelId: idSchema.nullish(),
        arrivalImagePosition: pointSchema.nullish(),
    }).refine(value => validTransitionPolygon(value.points), "Übergangsfläche ist ungültig")
        .refine(value => Boolean(value.targetLevelId) === Boolean(value.arrivalImagePosition),
            "Ziel-Level und Ankunftspunkt müssen gemeinsam gesetzt sein")),
    annotations: z.array(z.object({
        id: idSchema, position: pointSchema, kind: z.enum(["ROOM", "NOTE", "SECRET", "TRAP", "OTHER"]),
        label: z.string().max(20).nullish(), title: z.string().trim().min(1).max(160),
        text: z.string().max(20_000).nullish(),
    })),
    lights: z.array(z.object({
        id: idSchema, position: pointSchema,
        preset: z.enum(["CANDLE", "TORCH", "CAMPFIRE", "CUSTOM"]),
        rangeMeters: z.number().positive().max(100),
        intensity: z.number().min(0).max(1),
        colorStrength: z.number().min(0).max(1).optional(),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        enabled: z.boolean(),
        animation: z.object({effect: z.enum(["FLICKER", "PULSE"]),
            strength: z.number().min(0).max(1),
            speed: z.number().min(0.1).max(5)}).nullish(),
    })).default([]),
});

const dungeonMapSchema = z.object({
    image: imageSchema,
})

const dungeonLevelSchema = z.object({
    id: z.uuid(),
    name: z.coerce.string().min(3, "Level Name erforderlich"),
    sortIndex: z.coerce.number<number>().min(0),
    map: dungeonMapSchema,
    geometry: geometrySchema,
    geometryRevision: z.number().int().nonnegative().default(0),
})

export const dungeonSchema = z.object({
    id: z.uuid(),
    name: z.coerce.string().min(3, "Dungeon Name erforderlich"),
    coverImage: imageSchema.nullish(),
    levels: z.array(dungeonLevelSchema).min(1, "Mindestens 1 Level erforderlich").default([]),
}).superRefine((dungeon, context) => {
    const levels = new Map(dungeon.levels.map(level => [level.id, level]));
    dungeon.levels.forEach((source, levelIndex) => source.geometry.transitions.forEach((transition, index) => {
        if (!transition.targetLevelId) return;
        const target = levels.get(transition.targetLevelId);
        const path = ["levels", levelIndex, "geometry", "transitions", index, "targetLevelId"];
        if (!target) {
            context.addIssue({code: "custom", path, message: "Ziel muss ein Level dieses Dungeons sein"});
            return;
        }
        const arrival = transition.arrivalImagePosition;
        if (arrival && (arrival.x < 0 || arrival.y < 0 ||
            arrival.x > target.map.image.width || arrival.y > target.map.image.height)) {
            context.addIssue({code: "custom", path, message: "Ankunftspunkt liegt außerhalb der Zielkarte"});
        }
    }));
});

export type DungeonFormInput = z.input<typeof dungeonSchema>;
export type DungeonFormOutput = z.output<typeof dungeonSchema>;

export interface SaveDungeonRequest {
    id: string;
    name: string;
    coverImageId: string | null;
    levels: Array<{
        id: string;
        name: string;
        sortIndex: number;
        imageId: string;
        geometry: DungeonFormOutput["levels"][number]["geometry"];
        geometryRevision: number;
    }>;
}

export interface SaveDungeonBasicsRequest {
    id: string;
    name: string;
    coverImageId: string | null;
    levels: Array<{id: string; name: string; sortIndex: number; imageId: string}>;
}

export interface SaveDungeonGeometryRequest {
    levels: Array<{
        id: string;
        geometry: DungeonFormOutput["levels"][number]["geometry"];
        geometryRevision: number;
    }>;
}

export const toSaveDungeonBasicsRequest = (dungeon: DungeonFormOutput): SaveDungeonBasicsRequest => ({
    id: dungeon.id,
    name: dungeon.name,
    coverImageId: dungeon.coverImage?.id ?? null,
    levels: dungeon.levels.map((level, sortIndex) => ({
        id: level.id,
        name: level.name,
        sortIndex,
        imageId: level.map.image.id,
    })),
});

export const toSaveDungeonGeometryRequest = (dungeon: DungeonFormOutput): SaveDungeonGeometryRequest => ({
    levels: dungeon.levels.map(level => ({
        id: level.id,
        geometry: level.geometry,
        geometryRevision: level.geometryRevision,
    })),
});

export const toSaveDungeonRequest = (dungeon: DungeonFormOutput): SaveDungeonRequest => ({
    id: dungeon.id,
    name: dungeon.name,
    coverImageId: dungeon.coverImage?.id ?? null,
    levels: dungeon.levels.map((level, sortIndex) => ({
        id: level.id,
        name: level.name,
        sortIndex,
        imageId: level.map.image.id,
        geometry: level.geometry,
        geometryRevision: level.geometryRevision,
    })),
});


export const newDungeonLevel = (index: number) => ({
    id: UUID(), name: "", sortIndex: index, geometry: createEmptyLevelGeometry(), geometryRevision: 0,
});
